import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useGender } from "@/contexts/GenderContext";
import { useAuth } from "@/hooks/useAuth";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

export default function LandingNavigation() {
  const [location] = useLocation();
  const { selectedGender } = useGender();
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const getLogoSrc = () => {
    switch (selectedGender) {
      case "girl":
        return "/logo-pink.png";
      case "boy":
        return "/logo-blue.png";
      case "other":
        return "/logo-purple.png";
      default:
        return "/logo.png";
    }
  };

  const menuSections = [
    {
      title: "Product",
      links: [
        { href: "/features", label: "Features" },
        { href: "/pricing", label: "Pricing" },
        { href: "/gift-subscription", label: "Gift Subscriptions" },
      ],
    },
    {
      title: "Company",
      links: [
        { href: "/about", label: "About Us" },
        { href: "/community", label: "Community" },
        {
          href: "/snugglecrafters-influencer-program",
          label: "Influencer Program",
        },
        {
          href: "https://swag.SnuggleCrafters.com",
          label: "Swag Store",
          external: true,
        },
      ],
    },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2 cursor-pointer">
            <img
              src={getLogoSrc()}
              alt="SnuggleCrafters Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              SnuggleCrafters
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              href="/features"
              className="text-gray-700 hover:text-purple-600 transition-colors font-medium"
            >
              Features
            </Link>
            <Link
              href="/pricing"
              className="text-gray-700 hover:text-purple-600 transition-colors font-medium"
            >
              Pricing
            </Link>
            <Link
              href="/about"
              className="text-gray-700 hover:text-purple-600 transition-colors font-medium"
            >
              About
            </Link>
            <Link
              href="/community"
              className="text-gray-700 hover:text-purple-600 transition-colors font-medium"
            >
              Community
            </Link>
            {isAuthenticated ? (
              <>
                <Link href="/home">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-600 text-purple-600 hover:bg-purple-100"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {user?.firstName || "Dashboard"}
                  </Button>
                </Link>
                <Link href="/home">
                  <Button size="sm" variant="gradient">
                    Go to Dashboard
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link href="/auth">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-600 text-purple-600 hover:bg-purple-100"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link href="/auth?tab=register">
                  <Button size="sm" variant="gradient">
                    Start Free Trial
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[300px] sm:w-[400px] bg-white"
              >
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col space-y-6 mt-6">
                  {menuSections.map((section, idx) => (
                    <div key={idx} className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                        {section.title}
                      </h3>
                      <ul className="space-y-2">
                        {section.links.map((link, linkIdx) => (
                          <li key={linkIdx}>
                            {link.external ? (
                              <a
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block py-2 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors font-medium"
                                onClick={() => setIsOpen(false)}
                              >
                                {link.label}
                              </a>
                            ) : (
                              <Link
                                href={link.href}
                                className="block py-2 px-3 text-gray-700 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors font-medium"
                                onClick={() => setIsOpen(false)}
                              >
                                {link.label}
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  {/* CTA Buttons in Mobile Menu */}
                  <div className="pt-4 border-t border-gray-200 space-y-3">
                    {isAuthenticated ? (
                      <>
                        <div className="px-3 py-2 bg-purple-50 rounded-lg">
                          <p className="text-sm font-medium text-purple-900">
                            Signed in as {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-purple-600">
                            {user?.email}
                          </p>
                        </div>
                        <Link href="/home">
                          <Button
                            size="lg"
                            variant="gradient"
                            className="w-full"
                            onClick={() => setIsOpen(false)}
                          >
                            <User className="w-4 h-4 mr-2" />
                            Go to Dashboard
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link href="/auth">
                          <Button
                            size="lg"
                            variant="outline"
                            className="w-full border-purple-600 text-purple-600 hover:bg-purple-100"
                            onClick={() => setIsOpen(false)}
                          >
                            Sign In
                          </Button>
                        </Link>
                        <Link href="/auth">
                          <Button
                            size="lg"
                            variant="gradient"
                            className="w-full"
                            onClick={() => setIsOpen(false)}
                          >
                            Start Free Trial
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
